import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMutation } from "../../hooks/useExclusiveMutation";
import toast from "react-hot-toast";

import { eventPromoterAccountsApi } from "../../api";
import { queryKeys } from "../../queryKeys";
import type { EventPromoterAssignment } from "../../types";
import "./ticketTemplate.css";

type Props = {
  eventId: string;
  token?: string;
};

/** Owner-only: create/list/remove promoter LOGIN accounts for this event (distinct from the guest-attribution
 * "promoter" labels managed by EventPromoterPanel). */
export const EventPromoterAccountsPanel: React.FC<Props> = ({ eventId, token }) => {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [expanded, setExpanded] = useState(false);

  const accountsQuery = useQuery({
    queryKey: queryKeys.eventPromoterAccounts(eventId),
    queryFn: () => eventPromoterAccountsApi.list(eventId, token),
    enabled: Boolean(eventId),
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.eventPromoterAccounts(eventId) });
  };

  const createMutation = useMutation({
    mutationFn: () => eventPromoterAccountsApi.create(eventId, { email: email.trim() }, token),
    onSuccess: async (result) => {
      if (result.generated_password) {
        toast.success(
          `Promoter login created. One-time password: ${result.generated_password}`,
          { duration: 12000 },
        );
      } else {
        toast.success("Promoter added to this event");
      }
      setEmail("");
      await invalidate();
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Could not add promoter"),
  });

  const deleteMutation = useMutation({
    mutationFn: (promoterUserId: string) => eventPromoterAccountsApi.remove(eventId, promoterUserId, token),
    onSuccess: async () => {
      toast.success("Promoter access removed");
      await invalidate();
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Remove failed"),
  });

  const rows: EventPromoterAssignment[] = accountsQuery.data ?? [];

  return (
    <section className="event-promoter-panel" aria-label="Manage promoter logins">
      <div className="event-promoter-panel__head-row">
        <div>
          <h2 className="event-rsvp__manual-title">Promoter Accounts</h2>
          <p className="eventos-subtle event-rsvp__manual-hint">
            Give a promoter their own login so they can view this event&apos;s deal terms, P&amp;L and invoices.
          </p>
        </div>
        <button
          type="button"
          className="eventos-nav-btn"
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? "Collapse" : "Expand"}
        </button>
      </div>

      {expanded ? (
        <>
          <form
            className="event-promoter-panel__head"
            onSubmit={(e) => {
              e.preventDefault();
              if (!email.trim()) return;
              createMutation.mutate();
            }}
          >
            <label className="event-promoter-panel__add eventos-field">
              <span className="eventos-field-label">Promoter email</span>
              <input
                className="eventos-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="promoter@company.com"
                autoComplete="off"
              />
            </label>
            <button
              type="submit"
              className="eventos-primary-btn event-promoter-panel__add-btn"
              disabled={createMutation.isPending || !email.trim()}
            >
              Add Promoter
            </button>
          </form>

          <ul className="event-promoter-panel__list">
            {accountsQuery.isPending ? (
              <li className="eventos-subtle">Loading promoters…</li>
            ) : rows.length === 0 ? (
              <li className="eventos-subtle">No promoter accounts on this event yet.</li>
            ) : (
              rows.map((row) => (
                <li key={row.id} className="event-promoter-panel__item">
                  <span>
                    <strong>{row.promoter_name}</strong> — {row.promoter_email}
                  </span>
                  <button
                    type="button"
                    className="eventos-nav-btn eventos-nav-btn--danger event-promoter-panel__remove"
                    onClick={() => {
                      if (window.confirm(`Remove ${row.promoter_email} from this event?`)) {
                        deleteMutation.mutate(row.promoter_user_id);
                      }
                    }}
                  >
                    Remove
                  </button>
                </li>
              ))
            )}
          </ul>
        </>
      ) : null}
    </section>
  );
};
